import {
    IFrameHeightUpdateCommands,
    PartnershipSlotConfig,
    TemplateId,
} from '@gumtree/shared/src/types/advertising/partnership';
import {
    getSelectedPartner,
    SelectedPartner,
} from '@gumtree/shared/src/partnerships/categories-config/partner-selector';
import validate from '@gumtree/shared/src/partnerships/validator/get-validator';
import { filterByParamsList, transformObjectToString } from './query-parameters';
import paramsList from './params-list';

type SlotConfigBase = {
    width: string;
    height: string;
    resizeCommand?: IFrameHeightUpdateCommands;
    isResponsive: boolean;
    /**
     * Detect if ever visible using `IntersectionObserver` in parent document.
     * We'll send a message to the iframe when it is first visible.
     * Visible ratio defaults to `1` but can specify in `(0, 1]`.
     */
    detectSeen?: boolean | number;
};

export type AdSlotConfig = SlotConfigBase & {
    src: string;
};

export function findMatchingConfig(
    params: PartnershipSlotConfig
): (SlotConfigBase & { templateId: TemplateId }) | undefined {
    const { showCarMoneyMonthlyPrice, sellerType } = params;

    if (params.gaPageType === 'VIP' && params.l1Category === 'cars-vans-motorbikes') {
        const isMobile = params.deviceType === 'mobile';

        if (params.slotId === 'partnerAdCompact1') {
            return {
                templateId: 'vip-compact-confused',
                width: isMobile ? '50%' : '230px',
                height: '30px',
                isResponsive: true,
            };
        }

        if (params.slotId === 'partnerAdCompact2') {
            if (
                showCarMoneyMonthlyPrice &&
                !isMobile &&
                !['motors-accessories', 'motors-parts', 'cars-wanted'].includes(
                    params.l2Category!
                ) &&
                sellerType === 'Trade'
            ) {
                return {
                    templateId: 'vip-compact-carmoney-monthly-price',
                    width: isMobile ? '50%' : '230px',
                    height: '30px',
                    isResponsive: true,
                };
            } else {
                if (
                    params.l2Category === 'cars' ||
                    params.l2Category === 'vans' ||
                    params.l2Category === 'motorbikes-scooters'
                ) {
                    return {
                        templateId: 'vip-compact-carmoney',
                        width: isMobile ? '50%' : '230px',
                        height: '30px',
                        isResponsive: true,
                    };
                }
            }
        }
    }

    if (
        params.slotId === 'partnerAdDefault1' &&
        params.gaPageType === 'VIP' &&
        params.l1Category === 'cars-vans-motorbikes' &&
        !['motors-accessories', 'motors-parts', 'cars-wanted'].includes(params.l2Category!)
    ) {
        const deviceWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
        const height = deviceWidth < 1024 ? '1104px' : '568px';
        return {
            templateId: 'vip-default-carmoney',
            width: '100%',
            height,
            resizeCommand: IFrameHeightUpdateCommands.VIP_DEFAULT_CAR_MONEY_2021,
            isResponsive: true,
            detectSeen: 0.5,
        };
    }

    const selectedPartner: SelectedPartner = getSelectedPartner(
        params.gaPageType,
        params.l1Category,
        params.l2Category,
        params.l3Category
    );

    if (selectedPartner === SelectedPartner.ANYVAN) {
        if (params.slotId === 'partnerAdCompact1') {
            return {
                templateId: 'vip-compact-anyvan',
                width: '100%',
                height: '30px',
                isResponsive: true,
            };
        }

        if (params.slotId === 'partnerAdDefault1') {
            return {
                templateId: 'vip-default-anyvan',
                width: '100%',
                height: '324px',
                resizeCommand: IFrameHeightUpdateCommands.VIP_DEFAULT_ANYVAN,
                isResponsive: true,
            };
        }
    }

    return undefined;
}

export const encodeValues = (params: object): object => {
    return Object.entries(params)
        .filter(([_, value]) => Boolean(value))
        .reduce((acc, [key, value]) => {
            acc[key] = encodeURIComponent(value);
            return acc;
        }, {});
};

const slotConfig = (params: PartnershipSlotConfig): AdSlotConfig | undefined => {
    const foundConfig = findMatchingConfig(params);
    if (foundConfig) {
        const validationResult = validate(foundConfig.templateId)(params);

        if (validationResult.isValid) {
            const queryParameters = filterByParamsList(params, paramsList(foundConfig.templateId));

            return {
                src: `${params.baseUrl}/${foundConfig.templateId}?${transformObjectToString(
                    encodeValues(queryParameters)
                )}`,
                width: foundConfig.width,
                height: foundConfig.height,
                resizeCommand: foundConfig.resizeCommand,
                isResponsive: foundConfig.isResponsive,
                detectSeen: foundConfig.detectSeen,
            };
        }
    }

    return undefined;
};

export default slotConfig;
